/**
 * Shared product comparison card — Quick Winner + Auto Sourcing Results.
 * Canonical Deal Flow lead model only; never invents prices, IDs, or profit.
 */
import React from "react";
import { Bookmark, Bell, FolderPlus, LineChart, ScanSearch } from "lucide-react";
import { money } from "@/lib/format";
import { economicsKindLabel } from "@/lib/economicsLabels";
import { relativeAge } from "@/lib/dealFlow/metrics";
import { retailerName } from "@/lib/leads/retailerIdentity";
import { RatingStar } from "@/components/common/primitives";
import { ProductCardMedia } from "@/components/product-card/ProductCardMedia";
import { WalmartLogoMark } from "@/components/common/WalmartLogoMark";
import { IdentChip } from "@/components/product-card/IdentChip";
import { cn } from "@/lib/utils";
import "@/components/product-card/product-card.css";

function dash(v) {
  if (v === null || v === undefined || v === "") return "—";
  return v;
}

function pct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;
}

function spreadClass(spread, identityRejected) {
  // Stage Y P0-E: a spread between two DIFFERENT products is not an
  // opportunity. Keep the number for diagnostics, never style it as a win.
  if (identityRejected) return "pc-price-spread-neutral";
  if (spread == null || !Number.isFinite(Number(spread))) return "pc-price-spread-neutral";
  if (Number(spread) > 0) return "pc-price-spread-pos";
  if (Number(spread) < 0) return "pc-price-spread-neg";
  return "pc-price-spread-neutral";
}

function profitClass(profit, estimated, identityRejected) {
  if (identityRejected) return "text-ink-muted";
  if (profit == null || !Number.isFinite(Number(profit))) return "text-ink-muted";

  // SIGN BEFORE ESTIMATE.
  //
  // The estimate flag used to be checked first, so EVERY estimated figure was
  // blue — including losses. A card reading "est. -$3.70" in blue hides the one
  // fact the operator most needs, because blue reads as informational rather
  // than as "you would lose money here".
  //
  // A negative figure is a loss whether or not it is exact, so it stays red. The
  // blue treatment is still useful for the case it was meant for: a POSITIVE
  // number that is only an estimate must not look like confirmed green profit.
  // The "est." prefix and tooltip continue to mark the estimate itself.
  if (Number(profit) < 0) return "text-red-600";
  if (estimated) return "text-signal-blue";
  if (Number(profit) > 0) return "text-emerald-700";
  return "text-ink-primary";
}

function Metric({ label, children, valueClass, title }) {
  return (
    <div className="df-metric px-0.5 py-1 text-center border-r border-edge-subtle last:border-r-0" title={title}>
      <div className="text-[8px] font-semibold uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={cn("df-metric-value mt-0.5 text-[11px] font-bold tabular-nums", valueClass)}>
        {children}
      </div>
    </div>
  );
}

/** Prefer UPC; fall back to GTIN only when UPC absent (never invent). */
function displayUpc(lead) {
  const upc = lead?.upc != null ? String(lead.upc).trim() : "";
  if (upc) return upc;
  const gtin = lead?.gtin != null ? String(lead.gtin).trim() : "";
  return gtin || null;
}

/** Specific, honest state for an economics cell that has no number yet. */
export const ECONOMICS_STATUS_LABEL = {
  calculating_fees: "Calculating fees…",
  dimensions_needed: "Dimensions needed",
  fees_unavailable: "Fees unavailable",
  pack_verification_needed: "Pack check needed",
  missing_source_price: "No source price",
  missing_walmart_price: "No Walmart price",
  identity_rejected: "Unverified candidate",
};

export function economicsStatusLabel(lead) {
  const status = String(lead?.economicsStatus || "").trim();
  if (!status) return null;
  return ECONOMICS_STATUS_LABEL[status] || null;
}

export function DealFlowCard({ lead, onSave, onAnalyze, onOpen, onAddToCollection, className }) {
  const hasWalmart = Boolean(lead.walmartItemId);
  const upc = displayUpc(lead);
  const estimatedProfit =
    lead.economicsKind === "fee_excluded_estimate"
    || lead.opportunityStatus === "ESTIMATED"
    || (lead.profit != null && lead.economicsComplete === false)
    // An estimate attached to an unverified candidate is never confirmed profit.
    || (lead.profit != null && lead.candidateVerified === false);
  // Stage N: an incomplete economics cell names its actual missing input rather
  // than a generic "Fees needed". A number is shown whenever one exists.
  const statusLabel = economicsStatusLabel(lead);
  const feesNeeded = lead.profit == null && Boolean(statusLabel);
  const title = lead.walmartTitle || lead.sourceTitle;
  const brand = lead.walmartBrand || lead.sourceBrand;
  const checkedLabel = lead.checkedAt ? `Checked ${relativeAge(lead.checkedAt)}` : null;
  // Stage Z PR B: name the real retailer. "Source" hides which store the customer
  // is buying from, which is the whole point of a comparison card.
  const buyLabel = retailerName(lead.sourceId) || lead.sourceMerchant || "";
  // Stage Y P0-D: availability is available / out_of_stock / unknown. An unknown
  // or stale observation is NEVER rendered as in stock.
  const AVAILABILITY_LABELS = {
    in_stock: "In stock",
    out_of_stock: "Out of stock",
    unknown: "Stock unknown",
  };
  const availabilityState = (() => {
    const raw = String(lead.availabilityState || lead.walmartAvailability || "").toLowerCase();
    if (lead.availabilityStale) return "unknown";
    if (["in_stock", "in stock", "instock", "available"].includes(raw)) return "in_stock";
    if (["out_of_stock", "out of stock", "sold out", "unavailable", "not available"].includes(raw)) {
      return "out_of_stock";
    }
    return "unknown";
  })();
  const availabilityLabel = AVAILABILITY_LABELS[availabilityState];
  const availabilityTitle = lead.availabilityStale
    ? `Availability evidence is stale${lead.availabilityObservedAt ? ` (observed ${relativeAge(lead.availabilityObservedAt)})` : ""} — treated as unknown`
    : availabilityState === "unknown"
      ? "Walmart stock was not observed for this item"
      : `Walmart stock as observed${lead.availabilityObservedAt ? ` ${relativeAge(lead.availabilityObservedAt)}` : ""}`;
  // Stage X evidence line: how identity was verified, and whether fees are in.
  // Never a bare confidence percentage — a number is not a verification basis.
  const VERIFICATION_LABELS = {
    gtin: "UPC verified",
    upc: "UPC verified",
    ean: "EAN verified",
    model: "Model verified",
    mpn: "Model verified",
  };
  const verificationLabel = lead.verificationBasis
    ? `${VERIFICATION_LABELS[String(lead.verificationBasis).toLowerCase()] || "Identity verified"} · ${
        lead.feeCovered ? "fees in" : "fees est."
      }`
    : null;
  // Status/selling badges stay off the card chrome; reasons remain on the lead
  // for Product Analyzer / detail (filtering + validation unchanged).
  const reviewTitle =
    Array.isArray(lead.reviewReasonLabels) && lead.reviewReasonLabels.length
      ? lead.reviewReasonLabels.join(" · ")
      : lead.opportunityStatusHint || undefined;
  // Pack comparison line — only from persisted pack counts / unit prices.
  const sourcePack = lead.sourcePackLabel ?? lead.sourcePackCount;
  const walmartPack = lead.walmartPackLabel ?? lead.walmartPackCount;
  const packParts = [];
  if (sourcePack) packParts.push(`Source ${sourcePack}-pack`);
  if (walmartPack) packParts.push(`Walmart ${walmartPack}-pack`);
  const unitParts = [];
  if (lead.sourceUnitPrice != null) {
    unitParts.push(`${money(lead.sourceUnitPrice, lead.sourceCurrency)}/unit`);
  }
  if (lead.walmartUnitPrice != null) {
    unitParts.push(`${money(lead.walmartUnitPrice, lead.walmartCurrency)}/unit`);
  }
  const packLine =
    packParts.length || unitParts.length
      ? {
          packs: packParts.join(" · "),
          units: unitParts.length ? `${unitParts.join(" vs ")}` : null,
          title:
            lead.priceBasis === "unit"
              ? "Pack quantities differ — spread is per unit, not item-to-item."
              : "Pack quantities from the persisted listings.",
        }
      : null;

  return (
    <article
      className={cn(
        "df-product-card group bg-white border border-edge-subtle rounded-[10px] shadow-float flex flex-col h-full relative transition-[box-shadow,border-color] hover:shadow-lg hover:border-slate-300 cursor-pointer",
        className,
      )}
      data-testid={`deal-flow-card-${lead.id}`}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${title || lead.walmartItemId || "product"}`}
      title={reviewTitle}
      onClick={() => onOpen && onOpen(lead)}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && onOpen) {
          e.preventDefault();
          onOpen(lead);
        }
      }}
    >
      <ProductCardMedia
        sourceImage={lead.sourceImage}
        sourceImages={lead.sourceImages}
        sourceUrl={lead.sourceUrl}
        sourceMerchant={lead.sourceMerchant}
        walmartImage={lead.walmartImage}
        walmartImages={lead.walmartImages}
        walmartUrl={lead.walmartUrl}
        hasWalmart={hasWalmart}
      />

      {/* Source | Walmart | Spread */}
      <div className="pc-price-row" data-testid={`deal-flow-prices-${lead.id}`}>
        <div className="pc-price-col">
          <div
            className="pc-price-label"
            title={buyLabel ? `Buy from ${buyLabel}` : "Source retailer"}
            data-testid={`deal-flow-buy-retailer-${lead.id}`}
          >
            {buyLabel || "Source"}
            {lead.sourcePriceScope === "store_specific" && (
              <span
                className="ml-1 text-[8px] font-semibold uppercase tracking-wide text-amber-700"
                title={`Store-specific price${lead.sourcePriceLocationId ? ` (location ${lead.sourcePriceLocationId})` : ""} — not a nationwide online price`}
                data-testid={`deal-flow-store-price-${lead.id}`}
              >
                Store
              </span>
            )}
          </div>
          <div className="pc-price-value">{money(lead.sourcePrice, lead.sourceCurrency)}</div>
        </div>
        <div className={cn("pc-price-col", !hasWalmart && "opacity-55")}>
          <div className="pc-price-label pc-price-label-walmart">
            <WalmartLogoMark size={11} />
            <span>Walmart</span>
          </div>
          <div className="pc-price-value">{money(lead.walmartPrice, lead.walmartCurrency)}</div>
        </div>
        <div className="pc-price-col">
          <div
            className="pc-price-label"
            title={
              lead.identityRejected
                ? "These are different products. The price difference is shown for diagnostics only and is NOT a sourcing opportunity."
                : lead.priceBasis === "unit"
                  ? "Per-unit price difference (packs differ). Not profit."
                  : "Price difference only (Walmart − source). Not profit."
            }
          >
            {lead.identityRejected
              ? "Not comparable"
              : lead.priceBasis === "unit"
                ? "Spread / unit"
                : "Spread"}
          </div>
          <div className={cn("pc-price-value", spreadClass(lead.spread, lead.identityRejected))}>
            {money(lead.spread, lead.sourceCurrency)}
          </div>
        </div>
      </div>

      {/* Title + brand (no status / selling badges on the card surface) */}
      <div className="px-2.5 pt-1.5 pb-0.5">
        <p className="text-[11px] font-semibold text-ink-primary leading-snug line-clamp-2 m-0">
          {dash(title)}
        </p>
        <div className="mt-0.5">
          {brand ? (
            <span className="text-[10px] text-ink-muted whitespace-nowrap" title={brand}>
              Brand: {brand}
            </span>
          ) : (
            <span className="text-[10px] text-ink-muted">{dash(lead.sourceMerchant)}</span>
          )}
        </div>
      </div>

      {/* UPC + Walmart ID */}
      <div className="px-2.5 pb-1 flex gap-1 flex-wrap">
        <IdentChip label="UPC" value={upc} testId={`deal-flow-upc-${lead.id}`} />
        <IdentChip label="Walmart ID" value={lead.walmartItemId || null} testId={`deal-flow-wmid-${lead.id}`} />
      </div>

      {/* Pack Differs / unit-normalized comparison (shown only when supported) */}
      {packLine ? (
        <div
          className="px-2.5 pb-1 text-[10px] text-ink-muted leading-tight"
          data-testid={`deal-flow-pack-${lead.id}`}
          title={packLine.title}
        >
          {lead.packDiffers ? (
            <span className="df-pack-chip" data-testid={`deal-flow-pack-differs-${lead.id}`}>
              Pack Differs
            </span>
          ) : null}
          <span>{packLine.packs}</span>
          {packLine.units ? <span className="text-ink-muted"> · {packLine.units}</span> : null}
        </div>
      ) : null}

      {/* Profit / ROI / Margin / Match — spread already in the price row above */}
      <div className="df-stats border-t border-edge-subtle grid grid-cols-4 bg-surface-1">
        <Metric
          label="Profit"
          valueClass={profitClass(lead.profit, estimatedProfit, lead.identityRejected)}
          title={estimatedProfit ? economicsKindLabel(lead.economicsKind) || "Estimated — fulfillment fees excluded" : undefined}
        >
          {lead.profit != null ? (
            <>
              {estimatedProfit ? <span className="text-[8px] font-semibold mr-0.5">est.</span> : null}
              {money(lead.profit, lead.sourceCurrency)}
            </>
          ) : feesNeeded ? (
            <span className="df-fees-needed" data-testid={`deal-flow-economics-status-${lead.id}`}>
              {statusLabel}
            </span>
          ) : (
            "—"
          )}
        </Metric>
        <Metric label="ROI">{pct(lead.roi)}</Metric>
        <Metric label="Margin">{pct(lead.margin)}</Metric>
        <Metric label="Match">
          {lead.matchLabel
            ? lead.matchLabel
            : lead.matchConfidence != null
              ? `${Math.round(lead.matchConfidence)}%`
              : lead.potentialMatch
                ? "Potential"
                : lead.matchStatus === "verified"
                  ? "Verified"
                  : "—"}
        </Metric>
      </div>

      {/* Meta (reviews left · checked right) + centered actions */}
      <div className="pc-card-footer bg-white border-t border-edge-subtle rounded-b-[9px]">
        <div
          className="pc-card-meta"
          data-testid={`deal-flow-meta-${lead.id}`}
          data-opportunity-status={lead.opportunityStatus || undefined}
        >
          {lead.rating != null ? (
            <span className="pc-card-meta-rating whitespace-nowrap">
              <RatingStar />
              <b className="text-ink-primary font-semibold tabular-nums">{lead.rating.toFixed(1)}</b>
              {lead.reviews != null && (
                <span className="whitespace-nowrap">
                  · {Number(lead.reviews).toLocaleString()} reviews
                </span>
              )}
            </span>
          ) : (
            <span className="whitespace-nowrap text-ink-muted">Reviews unavailable</span>
          )}
          {lead.identityRejected ? (
            <span
              className="pc-card-meta-identity whitespace-nowrap text-red-600 font-semibold"
              data-testid={`deal-flow-identity-rejected-${lead.id}`}
              title="The source product and this Walmart listing are different products (brand, product type, variant, size or condition conflict)."
            >
              Not the same product
            </span>
          ) : null}
          {availabilityState !== "in_stock" ? (
            <span
              className={cn(
                "pc-card-meta-availability whitespace-nowrap",
                availabilityState === "out_of_stock" ? "text-red-600" : "text-ink-muted",
              )}
              data-testid={`deal-flow-availability-${lead.id}`}
              data-availability={availabilityState}
              title={availabilityTitle}
            >
              {availabilityLabel}
            </span>
          ) : null}
          {verificationLabel ? (
            <span
              className={cn("pc-card-meta-verify whitespace-nowrap", !lead.feeCovered && "text-amber-600")}
              data-testid={`deal-flow-verification-${lead.id}`}
              data-fee-covered={lead.feeCovered ? "true" : "false"}
              title={
                lead.feeCovered
                  ? "Identity established and fulfillment fees included"
                  : "Identity established, but fulfillment fees are not fully evidenced"
              }
            >
              {verificationLabel}
            </span>
          ) : null}
          {lead.condition && String(lead.condition).toLowerCase() !== "new" ? (
            <span
              className="pc-card-meta-condition whitespace-nowrap text-amber-600"
              data-testid={`deal-flow-condition-${lead.id}`}
              title="Listing condition differs from a new retail item"
            >
              {String(lead.condition).replace(/_/g, " ")}
            </span>
          ) : null}
          {checkedLabel ? (
            <span
              className="pc-card-meta-checked whitespace-nowrap"
              title="Last Walmart price / listing observation"
            >
              {checkedLabel}
            </span>
          ) : (
            <span className="pc-card-meta-checked text-ink-muted whitespace-nowrap">
              Not checked
            </span>
          )}
        </div>
        <div className="pc-card-actions">
          <button
            type="button"
            title={
              !lead.walmartItemId
                ? "Requires a matched Walmart listing"
                : lead.saving
                  ? "Saving…"
                  : lead.saved
                    ? "Remove from saved"
                    : "Save"
            }
            aria-pressed={Boolean(lead.saved)}
            aria-busy={Boolean(lead.saving)}
            // A pending save is disabled so a double click cannot create a
            // duplicate request; the icon state comes from the confirmed store.
            disabled={!lead.walmartItemId || Boolean(lead.saving)}
            onClick={(e) => {
              e.stopPropagation();
              onSave(lead);
            }}
            className={cn(
              "w-7 h-7 rounded-md border border-edge-subtle bg-white grid place-items-center shrink-0",
              !lead.walmartItemId && "opacity-40 cursor-not-allowed",
              lead.walmartItemId &&
                (lead.saved ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "hover:bg-emerald-50 hover:border-emerald-500"),
            )}
            data-testid={`deal-flow-save-${lead.id}`}
          >
            <Bookmark className="w-3.5 h-3.5" fill={lead.saved ? "currentColor" : "none"} />
          </button>
          {typeof onAddToCollection === "function" ? (
            <button
              type="button"
              title={
                lead.walmartItemId
                  ? "Add to collection"
                  : "Save this product first to organize it"
              }
              aria-label="Add to collection"
              disabled={!lead.walmartItemId}
              onClick={(e) => {
                e.stopPropagation();
                onAddToCollection(lead);
              }}
              className={cn(
                "w-7 h-7 rounded-md border border-edge-subtle bg-white grid place-items-center shrink-0",
                !lead.walmartItemId
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-emerald-50 hover:border-emerald-500",
              )}
              data-testid={`deal-flow-collection-${lead.id}`}
            >
              <FolderPlus className="w-3.5 h-3.5 text-slate-600" />
            </button>
          ) : null}
          <button
            type="button"
            title="Track price"
            aria-label="Track price"
            onClick={(e) => {
              e.stopPropagation();
              onOpen && onOpen(lead);
            }}
            className="w-7 h-7 rounded-md border border-edge-subtle bg-white grid place-items-center hover:bg-surface-1 shrink-0"
          >
            <Bell className="w-3.5 h-3.5 text-slate-600" />
          </button>
          <button
            type="button"
            title="Price history"
            aria-label="Price history"
            onClick={(e) => {
              e.stopPropagation();
              onOpen && onOpen(lead);
            }}
            className="w-7 h-7 rounded-md border border-edge-subtle bg-white grid place-items-center hover:bg-surface-1 shrink-0"
          >
            <LineChart className="w-3.5 h-3.5 text-slate-600" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAnalyze(lead);
            }}
            disabled={!lead.canAnalyze && !lead.walmartItemId && !displayUpc(lead)}
            className="bg-navy-900 text-white border-0 rounded-md px-2.5 py-1 text-[11px] font-semibold hover:bg-navy-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 shrink-0"
            data-testid={`deal-flow-analyze-${lead.id}`}
          >
            <ScanSearch className="w-3 h-3" /> Analyze
          </button>
        </div>
      </div>
    </article>
  );
}

export default DealFlowCard;
